import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import type { Href } from 'expo-router';
import { SquarePen } from 'lucide-react-native';

import { DirectoryListEmpty } from './DirectoryListEmpty';
import { DirectoryCard } from './directoryCard/DirectoryCard';
import { DirectoryModFilters, type CancerTypeLookup } from './directoryFilters';

import { TabRootFlatList, useIsTabRoot } from '@/components/TabRootList';
import { TrackedLink as TrackedRouterLink } from '@/components/TrackedLink';
import { Button, ButtonText } from '@/components/ui/button';
import { FlatList } from '@/components/ui/flat-list';
import { Icon } from '@/components/ui/icon';
import { LinkText } from '@/components/ui/link';
import { RefreshControl } from '@/components/ui/refresh-control';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { NAV_LABELS } from '@/constants/navigation';
import type { DirectoryItem } from '@/features/directory/model';
import { useColors } from '@/hooks/useColors';
import { useWhenToUseExplainerEnabled } from '@/hooks/useWhenToUseExplainerCopy';
import { segmentClient } from '@/util/analytics';

type Colors = ReturnType<typeof useColors>['colors'];

const directorySubtitleMobile =
  'Submit an inquiry about a patient case or a topic on your mind. Search by expert name, cancer type, or keyword.';

type DirectorySceneProps = {
  /** Lets the directory scroll this list, which it cannot reach through the scene boundary. */
  listRef?: React.Ref<FlatList<DirectoryItem>>;
  data: DirectoryItem[];
  isLoading: boolean;
  /** The search failed, so an empty list means it never ran rather than that nothing matched. */
  isError: boolean;
  onRetry: () => void;
  /** False until the directory has loaded once; placeholders stand in for search and filters until then. */
  isFetchingNextPage: boolean;
  refreshing: boolean;
  featuredExpertIds: Set<string>;
  buildInquiryUrl: (expertId: string | number) => Href;
  onRefresh: () => Promise<void>;
  onEndReached: () => void;
  onShowDemoModal: () => void;
  onResetSearch: () => void;
  filterProps: {
    focusId: string;
    setFocusId: (value: string) => void;
    selectedCancerTypeLabel: string;
    cancerTypeLookup: CancerTypeLookup | undefined;
    colors: Colors;
    bottom: number;
  };
  productTypeId: string;
  setProductTypeId: (value: string) => void;
  selectedProductTypeLabel: string;
};

/**
 * An empty list grows to fill the screen so its message can center, and still scrolls when the
 * message is taller than the room left under the header. `flex-1` would clamp the content to the
 * screen and leave the part past the tab bar out of reach.
 */
export const listContentClassName = (isEmpty: boolean) => (isEmpty ? 'grow gap-3' : 'gap-3');

/**
 * Directory scene component for the "All Experts" tab.
 * Displays searchable list of experts with filters.
 */
export function DirectoryScene({
  listRef,
  data,
  isLoading,
  isError,
  onRetry,
  isFetchingNextPage,
  refreshing,
  featuredExpertIds,
  buildInquiryUrl,
  onRefresh,
  onEndReached,
  onShowDemoModal,
  onResetSearch,
  filterProps,
  productTypeId,
  setProductTypeId,
  selectedProductTypeLabel,
}: DirectorySceneProps) {
  const isExplainerEnabled = useWhenToUseExplainerEnabled();

  const isEmpty = data.length === 0;

  const isTabRoot = useIsTabRoot();

  return (
    <TabRootFlatList
      ref={listRef}
      className="flex-1"
      contentContainerClassName={listContentClassName(isEmpty)}
      keyboardDismissMode="on-drag"
      showsVerticalScrollIndicator={false}
      stickyHeaderHiddenOnScroll
      /*
       * Not sticky on a tab root. The header there also carries the announcement banner and the
       * segment control, which would reappear over the list on every scroll up.
       */
      stickyHeaderIndices={isWeb || isTabRoot ? undefined : [0]}
      refreshControl={
        !isWeb ? <RefreshControl refreshing={refreshing} onRefresh={onRefresh} /> : undefined
      }
      ListHeaderComponent={
        <VStack className="bg-background-0">
          <VStack className="px-5 pb-4 pt-4" space="lg">
            <VStack>
              <Text className="text-base leading-normal text-typography-700">
                {directorySubtitleMobile}{' '}
                <LinkText
                  onPress={() => {
                    Promise.resolve(
                      segmentClient.track(ANALYTICS_EVENTS.INQUIRY_DEMO_OPENED, {
                        trigger_type: 'demo_link',
                      })
                    ).catch(() => undefined);

                    onShowDemoModal();
                  }}
                  className="font-normal text-base leading-normal tracking-normal text-info-700 underline data-[hover=true]:text-info-800">
                  See how it works
                </LinkText>
              </Text>
            </VStack>
            <DirectoryModFilters
              {...filterProps}
              productTypeId={productTypeId}
              setProductTypeId={setProductTypeId}
              selectedProductTypeLabel={selectedProductTypeLabel}
            />
            {isWeb ? (
              <TrackedRouterLink
                href="/inquiry"
                asChild
                trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
                trackingData={{ source: 'directory_mobile_header' }}>
                <Button size="sm" variant="solid">
                  <Icon as={SquarePen} className="text-typography-0" />
                  <ButtonText>{NAV_LABELS.inquiry.webNav}</ButtonText>
                </Button>
              </TrackedRouterLink>
            ) : null}
          </VStack>
        </VStack>
      }
      data={data}
      keyExtractor={(item, index) =>
        item.type === 'empty' ? item.id : (item.data.id?.toString() ?? `item-${index}`)
      }
      renderItem={({ item }) => (
        <VStack className="px-5">
          <DirectoryCard
            item={item}
            isMultiColumn={false}
            buildInquiryUrl={buildInquiryUrl}
            featuredExpertIds={featuredExpertIds}
            /*
             * This scene is the narrow layout, which has no Treatment Support
             * section to hang the new marker and the explainer link on, so the
             * card carries them here. The split layout puts them on the section
             * heading instead and leaves this false there, so exactly one of the
             * two renders them.
             */
            showExplainerAffordances={isExplainerEnabled}
          />
        </VStack>
      )}
      onEndReachedThreshold={0.8}
      onEndReached={onEndReached}
      ListEmptyComponent={
        <VStack className="grow px-5">
          <DirectoryListEmpty
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
            onResetSearch={onResetSearch}
          />
        </VStack>
      }
      ListFooterComponent={
        <VStack className="mb-4">{isFetchingNextPage ? <Spinner size="large" /> : null}</VStack>
      }
    />
  );
}
